import { makeAutoObservable, runInAction } from 'mobx'
import apiClient from '../utils/apiClient'
import resourceAssignmentApi from '../tenancy/resourceAssignmentApi'
import organizationSessionStore from './organizationSessionStore'
import { registerTenantScopedReset } from '../tenancy/tenantScopedState'
import { resourcePermissions } from '../tenancy/resourcePermissions'

function clientDto(value) {
	if (!value || !/^[1-9]\d*$/.test(String(value.id ?? ''))) throw new TypeError('Invalid client')
	return Object.freeze({ ...value, id: String(value.id), assigneeMembershipId: value.assigneeMembershipId == null ? null : String(value.assigneeMembershipId), permissions: resourcePermissions(value) })
}

export class ClientStore {
	clients = []
	selectedClient = null
	relatedRequests = []
	loading = false
	detailLoading = false
	error = null

	constructor({ http = apiClient, assignments = resourceAssignmentApi, session = organizationSessionStore } = {}) {
		this.http = http
		this.assignments = assignments
		this.session = session
		makeAutoObservable(this, { http: false, assignments: false, session: false }, { autoBind: true })
	}

	async load(query = '') {
		if (!this.session.isReady || this.loading) return null
		const generation = this.session.contextGeneration
		this.loading = true
		this.error = null
		try {
			const response = await this.http.get('/api/clients', { params: query ? { q: query } : undefined })
			const clients = Array.isArray(response.data?.clients) ? response.data.clients.map(clientDto) : []
			if (!this.isCurrent(generation)) return null
			runInAction(() => { this.clients = clients })
			return clients
		} catch (error) {
			if (this.isCurrent(generation)) runInAction(() => { this.clients = []; this.error = error?.response?.data?.error?.code || error.message })
			return null
		} finally {
			if (this.isCurrent(generation)) runInAction(() => { this.loading = false })
		}
	}

	async open(clientId) {
		if (!this.session.isReady) return null
		const generation = this.session.contextGeneration
		this.detailLoading = true
		this.error = null
		try {
			const [clientResponse, requestsResponse] = await Promise.all([
				this.http.get(`/api/clients/${clientId}`),
				this.http.get(`/api/clients/${clientId}/requests`),
			])
			if (!this.isCurrent(generation)) return null
			const client = clientDto(clientResponse.data?.client)
			const requests = Array.isArray(requestsResponse.data?.requests) ? requestsResponse.data.requests.map(value => Object.freeze({ ...value, id: String(value.id) })) : []
			runInAction(() => { this.selectedClient = client; this.relatedRequests = requests })
			return client
		} catch (error) {
			if (this.isCurrent(generation)) runInAction(() => { this.selectedClient = null; this.relatedRequests = []; this.error = error?.response?.data?.error?.code || error.message })
			return null
		} finally { if (this.isCurrent(generation)) runInAction(() => { this.detailLoading = false }) }
	}

	async create(data) { return this.mutate('post', '/api/clients', data, 'client') }
	async update(clientId, data) { return this.mutate('patch', `/api/clients/${clientId}`, data, 'client') }
	async archive(clientId) { return this.mutate('delete', `/api/clients/${clientId}`, undefined, null) }

	async mutate(method, url, data, responseKey) {
		const generation = this.session.contextGeneration
		this.error = null
		try {
			const response = await this.http[method](url, data)
			if (!this.isCurrent(generation)) return null
			if (responseKey && response.data?.[responseKey]) runInAction(() => { this.selectedClient = clientDto(response.data[responseKey]) })
			await this.load()
			return response.data?.[responseKey] || true
		} catch (error) {
			if (this.isCurrent(generation)) runInAction(() => { this.error = error?.response?.data?.error?.code || error.message })
			return null
		}
	}

	async assign(clientId, assigneeMembershipId) {
		const generation = this.session.contextGeneration
		try {
			await this.assignments.assign('clients', clientId, assigneeMembershipId)
			if (!this.isCurrent(generation)) return null
			return await this.load()
		} catch (error) {
			if (this.isCurrent(generation)) runInAction(() => { this.error = error?.response?.data?.error?.code || error.message })
			return null
		}
	}

	resetTenantUi() { this.clients = []; this.selectedClient = null; this.relatedRequests = []; this.loading = false; this.detailLoading = false; this.error = null }
	isCurrent(generation) { return generation === this.session.contextGeneration }
}

const clientStore = new ClientStore()
registerTenantScopedReset('clients', clientStore.resetTenantUi)
export default clientStore
